import { PaginationParams } from '@/types'

export const queryKeys = {
  admin: {
    multisigGroups: () => ['admin-multisig-groups'] as const,
  },

  multisig: {
    groupsListRoot: () => ['multisig-groups'] as const,
    groupsList: (pagination?: PaginationParams) =>
      ['multisig-groups', pagination ?? null] as const,

    groupByAccount: (account?: string | null) =>
      ['multisig-group', account ?? null] as const,

    proposalById: (proposalId?: string | null) =>
      ['multisig-proposal', proposalId ?? null] as const,

    proposalsListRoot: () => ['multisig-proposals'] as const,
    proposalsList: (pagination?: PaginationParams) =>
      ['multisig-proposals', pagination ?? null] as const,

    proposalVotes: (proposalId?: string | null) =>
      ['multisig-proposal-votes', proposalId ?? null] as const,

    proposalVoteByVoter: (proposalId?: string | null, voter?: string | null) =>
      ['multisig-proposal-voter', proposalId ?? null, voter ?? null] as const,
  },

  nft: {
    listRoot: () => ['nft-list'] as const,
    list: (pagination?: PaginationParams) =>
      ['nft-list', pagination ?? null] as const,
    listByOwner: (
      ownerAddress?: string | null,
      pagination?: PaginationParams,
    ) =>
      ['nft-list', 'owner', ownerAddress ?? null, pagination ?? null] as const,
    byAddress: (nftAddress?: string | null) =>
      ['nft', nftAddress ?? null] as const,
    rewardPerPeriod: (nftAddress?: string | null) =>
      ['nft', nftAddress ?? null, 'reward_per_period'] as const,
  },

  staking: {
    delegations: (delegatorAddress?: string | null) =>
      ['delegations', delegatorAddress ?? null] as const,
    unbondingDelegations: (delegatorAddress?: string | null) =>
      ['unbonding-delegations', delegatorAddress ?? null] as const,
    redelegations: (delegatorAddress?: string | null) =>
      ['redelegations', delegatorAddress ?? null] as const,
    params: () => ['staking-params'] as const,
    validatorsList: () => ['validators-list'] as const,
    validatorsIsUser: (walletAddress?: string | null) =>
      ['validators:is-user', walletAddress ?? null] as const,

    validatorCheck: (delegatorAddress: string) =>
      ['validator-check', delegatorAddress] as const,
  },

  bank: {
    balances: (accountAddress?: string | null) =>
      ['balances', accountAddress ?? null] as const,
  },

  rpc: {
    chainState: () => ['chain-state'] as const,
  },

  ipfs: {
    nftMetadata: (url?: string | null) =>
      ['nft-metadata', url ?? null] as const,
  },

  wallet: {
    keplrClient: (rpcUrl?: string | null) =>
      ['keplr-client', rpcUrl ?? null] as const,
    nftModuleParams: () => ['nft-module-params'] as const,
    keplrBalance: (
      walletAddress?: string | null,
      denom?: string | null,
      rpcUrl?: string | null,
    ) =>
      [
        'keplr-balance',
        walletAddress ?? null,
        denom ?? null,
        rpcUrl ?? null,
      ] as const,
  },

  tx: {
    mintNft: () => ['mint-nft'] as const,
    delegate: (nftAddress: string) => ['delegate-tx', nftAddress] as const,
    undelegate: (nftAddress: string) => ['undelegate-tx', nftAddress] as const,
    redelegate: (nftAddress: string) => ['redelegate-tx', nftAddress] as const,
    withdraw: (nftAddress: string) => ['withdraw-tx', nftAddress] as const,
    fund: (nftAddress: string) => ['fund-tx', nftAddress] as const,
    send: (nftAddress: string) => ['send-tx', nftAddress] as const,
    close: (nftAddress: string) => ['close-tx', nftAddress] as const,
    unjail: (validatorAddress: string) =>
      ['unjail-tx', validatorAddress] as const,
    nativeDelegate: (delegatorAddress: string) =>
      ['native-delegate-tx', delegatorAddress] as const,
    nativeUndelegate: (delegatorAddress: string) =>
      ['native-undelegate-tx', delegatorAddress] as const,
    nativeRedelegate: (delegatorAddress: string) =>
      ['native-redelegate-tx', delegatorAddress] as const,
  },

  governance: {
    proposals: (
      status?: string | null,
      voter?: string | null,
      depositor?: string | null,
      pagination?: PaginationParams,
    ) =>
      [
        'governance-proposals',
        status ?? null,
        voter ?? null,
        depositor ?? null,
        pagination ?? null,
      ] as const,
    proposal: (proposalId?: string | null) =>
      ['governance-proposal', proposalId ?? null] as const,
    votes: (proposalId?: string | null, pagination?: PaginationParams) =>
      ['governance-votes', proposalId ?? null, pagination ?? null] as const,
    vote: (proposalId?: string | null, voter?: string | null) =>
      ['governance-vote', proposalId ?? null, voter ?? null] as const,
  },

  tokens: {
    list: () => ['tokens'] as const,
    byId: (tokenId?: string | null) => ['token', tokenId ?? null] as const,
  },

  referrals: {
    list: () => ['referrals'] as const,
    byId: (referralId?: string | null) =>
      ['referral', referralId ?? null] as const,
  },

  chains: {
    list: () => ['chains'] as const,
  },
} as const
